import { XMLParser, XMLValidator } from 'fast-xml-parser';
import { ScoreParseError } from './errors';

/** A small, ordered XML tree. MusicXML depends on element order inside a measure. */
export interface XmlNode {
  name: string;
  attrs: Record<string, string>;
  children: XmlNode[];
  /** Concatenated text content of this element (not of its descendants). */
  text: string;
}

type OrderedItem = Record<string, unknown> & { ':@'?: Record<string, unknown> };

const parser = new XMLParser({
  preserveOrder: true,
  ignoreAttributes: false,
  attributeNamePrefix: '',
  parseTagValue: false,
  parseAttributeValue: false,
  trimValues: true,
  processEntities: true,
  ignoreDeclaration: true,
  ignorePiTags: true,
});

function convert(items: OrderedItem[]): XmlNode[] {
  const nodes: XmlNode[] = [];
  for (const item of items) {
    for (const key of Object.keys(item)) {
      if (key === ':@' || key === '#text' || key.startsWith('?') || key.startsWith('!')) continue;
      const rawChildren = item[key];
      const childItems = Array.isArray(rawChildren) ? (rawChildren as OrderedItem[]) : [];
      const attrs: Record<string, string> = {};
      for (const [name, value] of Object.entries(item[':@'] ?? {})) {
        attrs[name] = String(value);
      }
      let text = '';
      for (const child of childItems) {
        if ('#text' in child) text += String(child['#text']);
      }
      nodes.push({ name: key, attrs, children: convert(childItems), text: text.trim() });
    }
  }
  return nodes;
}

/** Parse XML text into a tree, rejecting text that is not well-formed. */
export function parseXml(xml: string): XmlNode[] {
  const valid = XMLValidator.validate(xml, { allowBooleanAttributes: true });
  if (valid !== true) {
    throw new ScoreParseError('malformedXml', valid.err.msg, valid.err.line ?? null);
  }
  return convert(parser.parse(xml) as OrderedItem[]);
}

export function child(node: XmlNode | undefined, name: string): XmlNode | undefined {
  return node?.children.find((c) => c.name === name);
}

export function childrenNamed(node: XmlNode | undefined, name: string): XmlNode[] {
  return node ? node.children.filter((c) => c.name === name) : [];
}

export function childText(node: XmlNode | undefined, name: string): string | null {
  const c = child(node, name);
  return c ? c.text : null;
}

export function childNumber(node: XmlNode | undefined, name: string): number | null {
  const t = childText(node, name);
  if (t === null || t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function hasChild(node: XmlNode | undefined, name: string): boolean {
  return child(node, name) !== undefined;
}
